// Yearly review (phase D): the pack is built by the server on demand; marking it done makes today the next baseline
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { CalendarPlus, ChevronDown, ChevronUp, ClipboardCheck } from "lucide-react";
import { useState, type ReactNode } from "react";
import { api, post } from "../../lib/api";
import { BAND_LABELS, CATEGORIES, CHECKLIST, eur, eurShort, errText, Flag, fmtDay, fmtStamp, Kpi, signed, WK, type Review, type WealthSettings } from "./shared";

// First working day on or after the due date (1 January is never it)
const reviewDay = (iso: string) => {
  const d = new Date(`${iso}T09:00:00`);
  if (d.getMonth() === 0 && d.getDate() === 1) d.setDate(2);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// One calendar event (.ics), 09:00–10:00 local, reminder an hour before
function downloadIcs(day: string) {
  const d = day.replace(/-/g, "");
  const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");
  const desc = "ONE → Wealth → Strategy → Yearly review. The pack is ready when you open it: what changed\\, where you stand against your policy\\, and the questions to decide.";
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Infinite Fusion//ONE Wealth//EN", "BEGIN:VEVENT",
    `UID:wealth-review-${d}@one.infinite-fusion.com`, `DTSTAMP:${stamp}`, `DTSTART:${d}T090000`, `DTEND:${d}T100000`,
    "SUMMARY:Yearly wealth review", `DESCRIPTION:${desc}`, `URL:${location.origin}/wealth?tab=strategy&review=1`,
    "BEGIN:VALARM", "TRIGGER:-PT60M", "ACTION:DISPLAY", "DESCRIPTION:Yearly wealth review", "END:VALARM",
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `wealth-review-${day}.ics`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const H4 = ({ children, note }: { children: ReactNode; note?: ReactNode }) => (
  <h4 className="mb-2 mt-5 text-sm font-bold first:mt-0">{children}{note && <span className="muted ml-1.5 text-xs font-normal">{note}</span>}</h4>
);
const RED = "#b91c1c", GREEN = "#065f46";

export function ReviewCard({ openInitially, saveSettings }: { openInitially: boolean; saveSettings: (b: Partial<WealthSettings>) => Promise<unknown> }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: WK.review, queryFn: () => api<Review>("/api/wealth/review"), refetchOnWindowFocus: false });
  const [open, setOpen] = useState(openInitially);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState<"" | "calendar" | "done">("");
  const [err, setErr] = useState("");
  const finish = useMutation({
    mutationFn: () => post("/api/wealth/review", { notes }),
    onSuccess: async () => {
      setNotes("");
      setBusy("done");
      setErr("");
      await Promise.all([qc.invalidateQueries({ queryKey: WK.review }), qc.invalidateQueries({ queryKey: WK.reviewStatus }), qc.invalidateQueries({ queryKey: WK.doc })]);
    },
    onError: (e) => setErr(errText(e)),
  });
  const rv = q.data;
  if (q.error && !rv) return <p className="muted text-sm">The yearly review is not available right now ({errText(q.error)}).</p>;
  if (!rv) return null;
  const stt = rv.status;
  const inCal = !!stt.calendarYear && stt.calendarYear >= Number(stt.nextDue.slice(0, 4));

  const addToCalendar = async () => {
    const day = reviewDay(stt.nextDue);
    setBusy("calendar");
    try {
      downloadIcs(day);
      await saveSettings({ reviewCalendarYear: Number(day.slice(0, 4)) });
      await qc.invalidateQueries({ queryKey: WK.review });
    } catch (e) {
      setErr(`Calendar: ${errText(e)}`);
    }
    setBusy("");
  };
  const markDone = () => {
    if (!window.confirm("Mark this year's review as done? Today's figures become the baseline for the next one.")) return;
    finish.mutate();
  };

  return (
    <section className={clsx("panel overflow-hidden", stt.due && "!border-amber-300 ring-1 ring-amber-200")}>
      <header className="flex flex-wrap items-center gap-3 px-5 py-4">
        <ClipboardCheck size={17} className={stt.due ? "text-amber-600" : "text-brand-600"} />
        <div className="min-w-0 flex-1">
          <div className="text-[0.9375rem] font-bold">Yearly review</div>
          <div className="muted text-xs">
            {stt.due ? `Due since ${fmtDay(stt.nextDue)}` : `Next one: ${stt.monthName} ${stt.nextDue.slice(0, 4)}, in ${stt.daysTo} days`}
            {stt.lastAt ? ` · last done ${fmtStamp(stt.lastAt)}` : " · never done yet — run one now to set the baseline"}
          </div>
        </div>
        {!stt.due &&
          (inCal ? (
            <span className="muted text-xs">In your calendar</span>
          ) : (
            <button className="btn btn-ghost btn-sm" disabled={busy === "calendar"} onClick={() => void addToCalendar()} title={`Calendar file (.ics), ${fmtDay(reviewDay(stt.nextDue))} 09:00, reminder an hour before`}>
              <CalendarPlus size={14} /> {busy === "calendar" ? "Adding…" : "Put it in my calendar"}
            </button>
          ))}
        <button
          className={clsx("btn btn-sm", stt.due && "btn-primary")}
          onClick={() => { setOpen((v) => !v); if (!open) void q.refetch(); }}
        >
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {open ? "Close" : stt.due ? "Start the review" : "Open the review pack"}
        </button>
      </header>
      {err && <p className="mx-5 mb-3 text-sm text-red-700">{err}</p>}
      {open && (
        <div className="border-t border-[var(--line)] px-5 py-4 text-sm">
          <H4 note={rv.baseline?.kind === "review" ? "your last review" : "the day the list began"}>1 · What changed {rv.baseline ? `since ${fmtStamp(rv.baseline.at)}` : ""}</H4>
          {rv.change && rv.baseline ? (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                <Kpi label="Assets" value={eurShort(rv.now.total)} sub={`${signed(rv.change.total)}${rv.change.totalPct != null ? ` (${rv.change.totalPct}%)` : ""} from ${eurShort(rv.baseline.total)}`} />
                <Kpi label="Including the companies" value={eurShort(rv.now.full)} sub={rv.change.full != null ? `${signed(rv.change.full)} since the last review` : "first time measured"} />
                {CATEGORIES.filter((c) => rv.change!.by[c.k]).slice(0, 3).map((c) => (
                  <Kpi key={c.k} label={c.label} value={signed(rv.change!.by[c.k]!)} tone={rv.change!.by[c.k]! < 0 ? "bad" : "good"} sub={`now ${eurShort(rv.now.by[c.k] || 0)}`} />
                ))}
              </div>
              {rv.movers.length > 0 && (
                <div className="mt-3 overflow-x-auto rounded-xl border border-[var(--line)]">
                  <table className="table">
                    <thead><tr><th>Item</th><th className="!text-right">Before</th><th className="!text-right">Now</th><th className="!text-right">Change</th></tr></thead>
                    <tbody>
                      {rv.movers.map((m) => (
                        <tr key={m.name}>
                          <td>{m.name}</td>
                          <td className="text-right num">{eur(m.before)}</td>
                          <td className="text-right num">{eur(m.after)}</td>
                          <td className="text-right num" style={{ color: m.delta < 0 ? RED : GREEN }}>{signed(m.delta)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!rv.movers.length && <p className="muted mt-2 text-[0.8125rem]">{rv.baseline.hasItems ? "No item has changed value since then." : "Item-by-item history starts with this review."}</p>}
              {(rv.added.length > 0 || rv.removed.length > 0) && (
                <p className="muted mt-2 text-[0.8125rem]">
                  {rv.added.length ? `Added: ${rv.added.map((a) => `${a.name} (${eurShort(a.value)})`).join(", ")}. ` : ""}
                  {rv.removed.length ? `Removed: ${rv.removed.map((a) => `${a.name} (${eurShort(a.value)})`).join(", ")}.` : ""}
                </p>
              )}
            </>
          ) : (
            <p className="muted text-[0.8125rem]">Nothing to compare with yet.</p>
          )}

          <H4>2 · Figures to refresh</H4>
          {rv.cashNotUpdated.length > 0 && <Flag pill="cash">{rv.cashNotUpdated.length} balance{rv.cashNotUpdated.length === 1 ? "" : "s"} older than 6 weeks: {rv.cashNotUpdated.map((a) => `${a.name} (${fmtDay(a.valuedOn)})`).join(", ")}.</Flag>}
          {rv.notRevalued.length > 0 && (
            <Flag pill="values">
              {rv.notRevalued.length} not revalued for over six months: {rv.notRevalued.slice(0, 12).map((a) => `${a.name} (${eurShort(a.value)})`).join(", ")}{rv.notRevalued.length > 12 ? ` and ${rv.notRevalued.length - 12} more` : ""}.
            </Flag>
          )}
          {rv.gaps.map((g) => <Flag key={g.key + g.text} pill={g.key}>{g.text} <span className="muted">{g.where}</span></Flag>)}
          {!rv.cashNotUpdated.length && !rv.notRevalued.length && !rv.gaps.length && <Flag pill="ok" ok>Every figure is current and nothing is missing.</Flag>}

          <H4>3 · The companies</H4>
          <p>
            {rv.companies.thisYear ? (
              <>
                Profit {rv.companies.thisYear.year}: <strong>{eur(rv.companies.thisYear.profit)}</strong> on {eurShort(rv.companies.thisYear.sales)} of sales
                {rv.companies.lastYear ? <> · {rv.companies.lastYear.year}: {eur(rv.companies.lastYear.profit)} on {eurShort(rv.companies.lastYear.sales)}</> : null}. Dividend
                {rv.companies.taxPct ? ` net of ${rv.companies.taxPct}%` : ", before tax"}: <strong>{eur(rv.companies.dividendNet)}</strong>. Valued at {eurShort(rv.now.companiesValue)}.
              </>
            ) : (
              <span className="muted">The CRM did not answer.</span>
            )}
          </p>

          <H4>4 · Against your policy</H4>
          <p>
            {rv.policy.map((p) => `${BAND_LABELS[p.key]} ${p.actual}% (${p.lo}–${p.hi}%)`).join(" · ")}. <strong>{rv.breaches} outside the band.</strong> Liquid {rv.now.liquidPct}%
            {rv.income.yearsCovered != null ? `, ${rv.income.yearsCovered} years of living costs` : ""}. {rv.income.cover != null ? `What the assets pay covers ${rv.income.cover}× your living costs.` : ""} In {rv.projection.headline.endYear} the projection reaches{" "}
            {eurShort(rv.projection.headline.endFull)}{rv.projection.isPlaceholder ? " on placeholder rates" : ""}.
          </p>

          <H4>5 · Protection</H4>
          <p>
            Duty on property at succession ≈ {eur(rv.succession.duty)}. Checklist {rv.succession.done}/{rv.succession.of}
            {rv.succession.done < rv.succession.of
              ? ` — open: ${rv.succession.checklist.filter((c) => !c.done).map((c) => (CHECKLIST.find((x) => x[0] === c.key)?.[1] || c.key).split(" (")[0]!.toLowerCase()).join("; ")}`
              : " — all done"}
            .
          </p>

          <H4 note="yours to answer — the app does not recommend">6 · Questions for you</H4>
          <ol className="list-decimal space-y-1.5 pl-5">{rv.questions.map((x, i) => <li key={i}>{x}</li>)}</ol>

          <H4>7 · What you decided</H4>
          <textarea className="input" rows={4} value={notes} placeholder="Your decisions and what changes — kept with this review, private." onChange={(e) => setNotes(e.target.value)} />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button className="btn btn-primary btn-sm" disabled={finish.isPending} onClick={markDone}><ClipboardCheck size={14} /> {finish.isPending ? "Saving…" : "Mark the review done"}</button>
            {busy === "done" && <span className="muted text-xs">Saved — today is now the baseline for the next review.</span>}
          </div>

          {rv.history.length > 0 && (
            <>
              <H4>Past reviews</H4>
              <div className="divide-line">
                {rv.history.map((r) => (
                  <div key={r.id} className="py-2 text-[0.8125rem]">
                    <strong>{fmtStamp(r.at)}</strong> · assets {eurShort(r.total)} · incl. companies {eurShort(r.full)} · {r.breaches} outside band{r.cover != null ? ` · cover ${r.cover}×` : ""}
                    {r.notes && <div className="muted mt-0.5 whitespace-pre-wrap">{r.notes}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
